import React, { useContext, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import { CookiesProvider } from "react-cookie";
import { AppDefs, GuideDefs } from "./components/icons/defs";

import AppLoader from "./components/layouts/app-loader";
import AuthPages from "./components/auth";

import { AppProviders, AppContexts } from "./providers";

import { PermissionsProvider, usePermissionsStore } from "./components/permissions/permissions_store";

/**
 * PermissionsSyncGate
 * Watches the auth context and loads effective permissions whenever
 * the authenticated user changes (login, logout, token refresh).
 * Must be rendered inside both AppProviders (for AuthContext) and
 * PermissionsProvider (for the store).
 */
function PermissionsSyncGate({ children }) {
  const authCtx = useContext(AppContexts.AuthContext);
  const { loadEffectivePermissions, loadRoles, resetPermissions } = usePermissionsStore();
  const [loadedIdentity, setLoadedIdentity] = useState(null);
  const accountId = authCtx.user?.account_id || authCtx.user?.user?.account_id || authCtx.user?.details?.account_id;
  const userId = authCtx.user?.user?.user_id;
  const identity = authCtx.user
    ? `${accountId}:${userId}`
    : null;
  const ownerId = authCtx.user?.user?.owner_id;
  const authenticatedUser = useMemo(() => ({
    user_id: userId,
    account_id: accountId,
    owner_id: ownerId,
  }), [accountId, userId, ownerId]);

  useEffect(() => {
    let active = true;
    resetPermissions();
    setLoadedIdentity(null);
    if (identity) {
      loadEffectivePermissions(authenticatedUser).then(() => { if (active) setLoadedIdentity(identity); });
      loadRoles();
    }
    return () => { active = false; resetPermissions(); };
  }, [identity, authenticatedUser, loadEffectivePermissions, loadRoles, resetPermissions]);

  useEffect(() => {
    if (!identity) return undefined;
    const refresh = () => {
      if (document.visibilityState !== "hidden") loadEffectivePermissions(authenticatedUser);
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    const timer = window.setInterval(refresh, 60000);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.clearInterval(timer);
    };
  }, [identity, authenticatedUser, loadEffectivePermissions]);

  if (identity && loadedIdentity !== identity) return null;

  return children;
}

const Help = React.lazy(() => import("./components/help"));

function App() {
  return (
    <BrowserRouter>
      <Switch>
        <Route path="/help" render={() => (
          <React.Suspense fallback={<p role="status">Loading Help Center…</p>}>
            <Help />
          </React.Suspense>
        )} />
        <Route render={() => (
          <AppProviders>
            <PermissionsProvider>
              <CookiesProvider>
                <PermissionsSyncGate>
                  <AppDefs />
                  <GuideDefs />
                  <Switch>
                    <Route path="/auth" component={AuthPages} />
                    <Route path="/" render={(props) => <AppLoader {...props} />} />
                  </Switch>
                </PermissionsSyncGate>
              </CookiesProvider>
            </PermissionsProvider>
          </AppProviders>
        )} />
      </Switch>
    </BrowserRouter>
  );
}

export default App;
